import { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./AdminShared.module.css";
import { useAdminUI } from "./AdminUIContext";
import { downloadJson, itemsToArray } from "./adminUtils";

const AdminTools = () => {
  const { toast, confirm } = useAdminUI();
  const catalog = useSelector((state) => state.catalogData.catalog);
  const content = useSelector((state) => state.contentData.content);
  const catalogMetadata = useSelector(
    (state) => state.catalogData.catalogMetadata,
  );
  const contentMetadata = useSelector(
    (state) => state.contentData.contentMetadata,
  );
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    window.dispatchEvent(new CustomEvent("ms1-admin-refresh-data"));
    toast.info("Refreshing catalog and content from the server…");
    window.setTimeout(() => setRefreshing(false), 1200);
  };

  const handleExport = async (which) => {
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    if (which === "catalog") {
      downloadJson(
        {
          exportedAt: new Date().toISOString(),
          catalog: itemsToArray(catalog),
          catalogMetadata,
        },
        `ignite-elysium-catalog-${stamp}.json`,
      );
      toast.success("Catalog JSON downloaded.");
      return;
    }
    if (which === "content") {
      downloadJson(
        {
          exportedAt: new Date().toISOString(),
          content: itemsToArray(content),
          contentMetadata,
        },
        `ignite-elysium-content-${stamp}.json`,
      );
      toast.success("Content JSON downloaded.");
      return;
    }
    downloadJson(
      {
        exportedAt: new Date().toISOString(),
        catalog: itemsToArray(catalog),
        content: itemsToArray(content),
        catalogMetadata,
        contentMetadata,
      },
      `ignite-elysium-full-export-${stamp}.json`,
    );
    toast.success("Full export downloaded.");
  };

  const handleDangerNote = async () => {
    await confirm({
      title: "Bulk delete not available here",
      message:
        "Destructive “delete all” operations are intentionally omitted from this admin UI. Use the database tools or a controlled backend script if you need a full wipe.",
      confirmLabel: "Understood",
      cancelLabel: "Close",
    });
  };

  return (
    <div>
      <div className={styles["page-header"]}>
        <div className={styles["page-header-content"]}>
          <h1 className={styles["page-title"]}>Tools</h1>
          <p className={styles["page-subtitle"]}>
            Refresh live data, export backups, and jump to the public site.
          </p>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles["panel-header"]}>
          <h2 className={styles["panel-title"]}>Data</h2>
          <div className={styles["page-actions"]}>
            <button
              type="button"
              className={styles["btn-primary"]}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing…" : "Refresh from server"}
            </button>
          </div>
        </div>
        <p className={styles["field-hint"]}>
          Pulls the latest catalog and content into the app without a full page
          reload.
        </p>
      </div>

      <div className={styles.panel}>
        <div className={styles["panel-header"]}>
          <h2 className={styles["panel-title"]}>Export JSON</h2>
          <div className={styles["page-actions"]}>
            <button
              type="button"
              className={styles["btn-secondary"]}
              onClick={() => handleExport("catalog")}
            >
              Export catalog
            </button>
            <button
              type="button"
              className={styles["btn-secondary"]}
              onClick={() => handleExport("content")}
            >
              Export content
            </button>
            <button
              type="button"
              className={styles["btn-secondary"]}
              onClick={() => handleExport("all")}
            >
              Export all
            </button>
          </div>
        </div>
        <p className={styles["field-hint"]}>
          Downloads the data currently loaded in the browser (after last
          refresh).
        </p>
      </div>

      <div className={styles.panel}>
        <h2 className={styles["panel-title"]}>Shortcuts</h2>
        <ul className={styles["quick-list"]}>
          <li>
            <Link to="/">View public site →</Link>
          </li>
          <li>
            <Link to="/manage/tracking">Tracking →</Link>
          </li>
          <li>
            <Link to="/manage/help">Admin help →</Link>
          </li>
          <li>
            <Link to="/manage/catalog/new">New catalog item →</Link>
          </li>
          <li>
            <Link to="/manage/content/new">New content item →</Link>
          </li>
        </ul>
      </div>

      <div className={styles.panel}>
        <div className={styles["panel-header"]}>
          <h2 className={styles["panel-title"]}>Danger zone</h2>
          <div className={styles["page-actions"]}>
            <button
              type="button"
              className={styles["btn-ghost"]}
              onClick={handleDangerNote}
            >
              Why can&apos;t I delete everything?
            </button>
          </div>
        </div>
        <p className={styles["field-hint"]}>
          Mass-delete endpoints exist on the API but are not exposed in this UI
          to prevent accidents.
        </p>
      </div>
    </div>
  );
};

export default AdminTools;
